import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  DollarSign,
  PiggyBank,
  Receipt,
  AlertTriangle,
  CheckCircle2,
  AlertCircle,
  Settings,
  RefreshCw,
  Loader2,
  Building2,
  Users,
  Info,
  Mail,
  Send,
  Calendar,
  Clock,
  X,
  Plus,
  FileDown,
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const ReserveFundIndicator = ({ status, percentage }) => {
  const configs = {
    healthy: { color: 'text-emerald-600', bg: 'bg-emerald-100', icon: CheckCircle2, label: 'Healthy' },
    needs_improvement: { color: 'text-amber-600', bg: 'bg-amber-100', icon: AlertCircle, label: 'Needs Improvement' },
    critical: { color: 'text-red-600', bg: 'bg-red-100', icon: AlertTriangle, label: 'Critical' },
  };
  const config = configs[status] || configs.needs_improvement;
  const Icon = config.icon;

  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${config.bg}`}>
      <Icon className={`w-4 h-4 ${config.color}`} />
      <span className={`text-sm font-medium ${config.color}`}>{config.label}</span>
      <span className={`text-sm ${config.color}`}>({percentage}%)</span>
    </div>
  );
};

export default function FinancialDashboard() {
  const navigate = useNavigate();
  const { hoaId: paramHoaId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [showReserveModal, setShowReserveModal] = useState(false);
  const [reserveForm, setReserveForm] = useState({ target_amount: 0, current_amount: 0 });
  const [saving, setSaving] = useState(false);
  
  // Email report state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportSchedule, setReportSchedule] = useState(null);
  const [reportForm, setReportForm] = useState({
    enabled: false,
    frequency: 'monthly',
    day_of_month: 1,
    recipients: [],
    include_levy_status: true,
    include_expense_breakdown: true,
    include_reserve_fund: true,
    include_trends: true,
    include_pdf: false,
  });
  const [newRecipient, setNewRecipient] = useState('');
  const [sendingReport, setSendingReport] = useState(false);
  const [reportHistory, setReportHistory] = useState([]);

  const hoaId = paramHoaId || user?.hoa_id || 'demo_hoa';
  const isFullAccess = ['trustee', 'property_manager', 'managing_agent', 'agent_admin', 'platform_owner'].includes(user?.role);

  useEffect(() => {
    fetchDashboardData();
    if (isFullAccess) {
      fetchReportSchedule();
      fetchReportHistory();
    }
  }, [hoaId]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/financials/dashboard/${hoaId}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch dashboard data');
      const data = await response.json();
      setDashboardData(data);
      if (data.reserve_fund) {
        setReserveForm({
          target_amount: data.reserve_fund.target_amount || 0,
          current_amount: data.reserve_fund.current_amount || 0,
        });
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error('Failed to load financial dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchReportSchedule = async () => {
    try {
      const response = await fetch(`${API_URL}/api/financials/report-schedule/${hoaId}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setReportSchedule(data);
        setReportForm({
          enabled: data.enabled || false,
          frequency: data.frequency || 'monthly',
          day_of_month: data.day_of_month || 1,
          recipients: data.recipients || [],
          include_levy_status: data.include_levy_status !== false,
          include_expense_breakdown: data.include_expense_breakdown !== false,
          include_reserve_fund: data.include_reserve_fund !== false,
          include_trends: data.include_trends !== false,
          include_pdf: data.include_pdf || false,
        });
      }
    } catch (error) {
      console.error('Failed to fetch report schedule:', error);
    }
  };

  const fetchReportHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/financials/report-history/${hoaId}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setReportHistory(data);
      }
    } catch (error) {
      console.error('Failed to fetch report history:', error);
    }
  };

  const handleSaveReserveFund = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/financials/dashboard/${hoaId}/reserve-fund`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(reserveForm),
      });
      if (!response.ok) throw new Error('Failed to update reserve fund');
      toast.success('Reserve fund updated successfully');
      setShowReserveModal(false);
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to update reserve fund');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveReportSchedule = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/financials/report-schedule/${hoaId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(reportForm),
      });
      if (!response.ok) throw new Error('Failed to update schedule');
      const data = await response.json();
      toast.success('Report schedule updated');
      setShowReportModal(false);
      fetchReportSchedule();
    } catch (error) {
      toast.error('Failed to update report schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleSendReportNow = async () => {
    setSendingReport(true);
    try {
      const response = await fetch(`${API_URL}/api/financials/send-report/${hoaId}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to send report');
      }
      const data = await response.json();
      toast.success(data.message);
      fetchReportHistory();
    } catch (error) {
      toast.error(error.message || 'Failed to send report');
    } finally {
      setSendingReport(false);
    }
  };

  const addRecipient = () => {
    if (newRecipient && newRecipient.includes('@')) {
      if (!reportForm.recipients.includes(newRecipient)) {
        setReportForm({ ...reportForm, recipients: [...reportForm.recipients, newRecipient] });
      }
      setNewRecipient('');
    }
  };

  const removeRecipient = (email) => {
    setReportForm({ ...reportForm, recipients: reportForm.recipients.filter(r => r !== email) });
  };

  const getBackPath = () => {
    if (user?.role === 'resident') return '/dashboard';
    if (['managing_agent', 'agent_admin'].includes(user?.role)) return '/agent';
    return '/admin';
  };

  const exportPdf = () => {
    try {
      const doc = new jsPDF();
      const title = `Financial Statement — ${dashboardData?.building_name || 'Building'}`;
      const period = dashboardData?.period || '';
      doc.setFontSize(16);
      doc.text(title, 14, 18);
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Period: ${period}`, 14, 25);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30);

      // Summary table
      const summary = [
        ['Levies Billed', formatCurrency(dashboardData?.levies_billed || 0)],
        ['Levies Collected', formatCurrency(dashboardData?.levies_collected || 0)],
        ['Collection Rate', `${dashboardData?.collection_rate || 0}%`],
        ['Reserve Fund', formatCurrency(dashboardData?.reserve_fund_balance || 0)],
        ['Total Units', String(dashboardData?.total_units || 0)],
        ['Occupied Units', String(dashboardData?.occupied_units || 0)],
      ];
      autoTable(doc, {
        startY: 38,
        head: [['Metric', 'Value']],
        body: summary,
        theme: 'striped',
        headStyles: { fillColor: [79, 70, 229] },
      });

      // Expense breakdown table
      const breakdown = (dashboardData?.expense_breakdown || []).map(c => [
        c.name,
        formatCurrency(c.amount || 0),
        `${c.percentage || 0}%`,
      ]);
      if (breakdown.length) {
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 10,
          head: [['Expense Category', 'Amount', 'Share']],
          body: breakdown,
          theme: 'striped',
          headStyles: { fillColor: [79, 70, 229] },
        });
      }

      const filename = `financial-statement-${(dashboardData?.building_name || 'building').replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`;
      doc.save(filename);
      toast.success('PDF downloaded');
    } catch (e) {
      console.error('PDF export failed', e);
      toast.error('Failed to generate PDF');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Data Available</h2>
            <p className="text-slate-600 mb-4">Financial dashboard data is not yet available for this building.</p>
            <Button onClick={() => navigate(getBackPath())}>Go Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isLimitedView = dashboardData.view_type === 'limited';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(getBackPath())} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Financial Dashboard</h1>
                <p className="text-sm text-slate-500">{dashboardData.period}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {dashboardData.is_demo && (
                <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                  Demo Data
                </span>
              )}
              {isFullAccess && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportPdf()}
                  data-testid="download-pdf-btn"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              )}
              {isFullAccess && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowReportModal(true)}
                  data-testid="email-reports-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email Reports
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={fetchDashboardData} data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Limited View Notice for Residents */}
        {isLimitedView && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-800 font-medium">Limited View</p>
              <p className="text-blue-600 text-sm">As a resident, you can see financial health percentages. Contact your trustees for detailed financial information.</p>
            </div>
          </div>
        )}

        {/* Key Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Levy Collection */}
          <Card className="border-0 shadow-lg" data-testid="levy-collection-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Receipt className="w-6 h-6 text-blue-600" />
                </div>
                {dashboardData.levy_collection?.collection_percentage >= 90 ? (
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-amber-500" />
                )}
              </div>
              <h3 className="text-sm font-medium text-slate-500 mb-1">Levy Collection Rate</h3>
              <p className="text-3xl font-bold text-slate-900">
                {dashboardData.levy_collection?.collection_percentage}%
              </p>
              {!isLimitedView && dashboardData.levy_collection?.outstanding && (
                <p className="text-sm text-slate-500 mt-2">
                  Outstanding: {formatCurrency(dashboardData.levy_collection.outstanding)}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Total Collected (Full view only) */}
          {!isLimitedView && dashboardData.levy_collection?.total_collected && (
            <Card className="border-0 shadow-lg" data-testid="total-collected-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
                <h3 className="text-sm font-medium text-slate-500 mb-1">Total Collected (YTD)</h3>
                <p className="text-3xl font-bold text-slate-900">
                  {formatCurrency(dashboardData.levy_collection.total_collected)}
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  of {formatCurrency(dashboardData.levy_collection.total_billed)} billed
                </p>
              </CardContent>
            </Card>
          )}

          {/* Reserve Fund */}
          <Card className="border-0 shadow-lg" data-testid="reserve-fund-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <PiggyBank className="w-6 h-6 text-purple-600" />
                </div>
                {isFullAccess && (
                  <Button variant="ghost" size="icon" onClick={() => setShowReserveModal(true)} data-testid="edit-reserve-btn">
                    <Settings className="w-4 h-4 text-slate-400" />
                  </Button>
                )}
              </div>
              <h3 className="text-sm font-medium text-slate-500 mb-1">Reserve Fund Health</h3>
              <ReserveFundIndicator 
                status={dashboardData.reserve_fund?.status} 
                percentage={dashboardData.reserve_fund?.percentage} 
              />
              {!isLimitedView && dashboardData.reserve_fund?.current_amount && (
                <p className="text-sm text-slate-500 mt-3">
                  {formatCurrency(dashboardData.reserve_fund.current_amount)} of {formatCurrency(dashboardData.reserve_fund.target_amount)} target
                </p>
              )}
            </CardContent>
          </Card>

          {/* Units Info (Full view only) */}
          {!isLimitedView && dashboardData.levy_collection?.units_total && (
            <Card className="border-0 shadow-lg" data-testid="units-info-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-slate-600" />
                  </div>
                </div>
                <h3 className="text-sm font-medium text-slate-500 mb-1">Building Info</h3>
                <p className="text-3xl font-bold text-slate-900">
                  {dashboardData.levy_collection.units_total} Units
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  Levy: {formatCurrency(dashboardData.levy_collection.levy_per_unit)}/unit/month
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Expense Breakdown Pie Chart */}
          <Card className="border-0 shadow-lg" data-testid="expense-breakdown-card">
            <CardHeader>
              <CardTitle className="text-lg">Monthly Expense Breakdown</CardTitle>
              <CardDescription>How funds are allocated across categories</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={dashboardData.expense_breakdown}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percentage }) => `${name}: ${percentage}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey={isLimitedView ? "percentage" : "amount"}
                      nameKey="name"
                    >
                      {dashboardData.expense_breakdown?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [
                        isLimitedView ? `${value}%` : formatCurrency(value),
                        name
                      ]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Expense Categories List */}
          <Card className="border-0 shadow-lg" data-testid="expense-list-card">
            <CardHeader>
              <CardTitle className="text-lg">Expense Categories</CardTitle>
              <CardDescription>Detailed breakdown by category</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {dashboardData.expense_breakdown?.map((category) => (
                  <div key={category.category} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: category.color }}
                      />
                      <span className="text-slate-700">{category.name}</span>
                    </div>
                    <div className="text-right">
                      {!isLimitedView && (
                        <p className="font-semibold text-slate-900">{formatCurrency(category.amount)}</p>
                      )}
                      <p className="text-sm text-slate-500">{category.percentage}%</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Trends Chart (Full view only) */}
        {!isLimitedView && dashboardData.monthly_trends && (
          <Card className="border-0 shadow-lg" data-testid="trends-card">
            <CardHeader>
              <CardTitle className="text-lg">Maintenance Spending Trends</CardTitle>
              <CardDescription>Monthly maintenance costs over time</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dashboardData.monthly_trends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" stroke="#64748b" />
                    <YAxis stroke="#64748b" tickFormatter={(v) => `R${(v/1000).toFixed(0)}k`} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      labelStyle={{ color: '#1e293b' }}
                    />
                    <Legend />
                    <Bar dataKey="maintenance_cost" name="Maintenance" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Levy Collection Trend (Full view only) */}
        {!isLimitedView && dashboardData.monthly_trends && (
          <Card className="border-0 shadow-lg mt-6" data-testid="levy-trends-card">
            <CardHeader>
              <CardTitle className="text-lg">Levy Collection Trends</CardTitle>
              <CardDescription>Monthly levy billed vs collected</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dashboardData.monthly_trends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" stroke="#64748b" />
                    <YAxis stroke="#64748b" tickFormatter={(v) => `R${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="levies_billed" 
                      name="Billed" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      dot={{ fill: '#3b82f6' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="levies_collected" 
                      name="Collected" 
                      stroke="#10b981" 
                      strokeWidth={2}
                      dot={{ fill: '#10b981' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Reserve Fund Edit Modal */}
      <Dialog open={showReserveModal} onOpenChange={setShowReserveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Reserve Fund</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Target Amount (R)</Label>
              <Input
                type="number"
                value={reserveForm.target_amount}
                onChange={(e) => setReserveForm({ ...reserveForm, target_amount: parseFloat(e.target.value) || 0 })}
                placeholder="500000"
                data-testid="reserve-target-input"
              />
              <p className="text-sm text-slate-500 mt-1">The ideal reserve fund balance for your building</p>
            </div>
            <div>
              <Label>Current Amount (R)</Label>
              <Input
                type="number"
                value={reserveForm.current_amount}
                onChange={(e) => setReserveForm({ ...reserveForm, current_amount: parseFloat(e.target.value) || 0 })}
                placeholder="350000"
                data-testid="reserve-current-input"
              />
              <p className="text-sm text-slate-500 mt-1">Current reserve fund balance</p>
            </div>
            {reserveForm.target_amount > 0 && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  Health: <strong>{((reserveForm.current_amount / reserveForm.target_amount) * 100).toFixed(1)}%</strong>
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReserveModal(false)}>Cancel</Button>
            <Button onClick={handleSaveReserveFund} disabled={saving} data-testid="save-reserve-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Report Schedule Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Email Financial Reports
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Automated Reports</p>
                <p className="text-sm text-slate-500">Send monthly financial summaries to trustees</p>
              </div>
              <Switch
                checked={reportForm.enabled}
                onCheckedChange={(checked) => setReportForm({ ...reportForm, enabled: checked })}
                data-testid="report-enabled-switch"
              />
            </div>

            {/* Schedule Settings */}
            {reportForm.enabled && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Frequency</Label>
                    <select
                      className="w-full mt-1 p-2 border border-slate-200 rounded-lg bg-white"
                      value={reportForm.frequency}
                      onChange={(e) => setReportForm({ ...reportForm, frequency: e.target.value })}
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                    </select>
                  </div>
                  <div>
                    <Label>Day of Month</Label>
                    <Input
                      type="number"
                      min={1}
                      max={28}
                      value={reportForm.day_of_month}
                      onChange={(e) => setReportForm({ ...reportForm, day_of_month: parseInt(e.target.value) || 1 })}
                      data-testid="report-day-input"
                    />
                  </div>
                </div>

                {/* Recipients */}
                <div>
                  <Label>Recipients</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="email"
                      placeholder="trustee@example.com"
                      value={newRecipient}
                      onChange={(e) => setNewRecipient(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addRecipient()}
                      data-testid="add-recipient-input"
                    />
                    <Button variant="outline" size="icon" onClick={addRecipient}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {reportForm.recipients.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {reportForm.recipients.map((email) => (
                        <span 
                          key={email} 
                          className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full"
                        >
                          {email}
                          <button onClick={() => removeRecipient(email)} className="hover:text-blue-900">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Include Sections */}
                <div>
                  <Label className="mb-2 block">Include in Report</Label>
                  <div className="space-y-2">
                    {[
                      { key: 'include_levy_status', label: 'Levy Collection Status' },
                      { key: 'include_expense_breakdown', label: 'Expense Breakdown' },
                      { key: 'include_reserve_fund', label: 'Reserve Fund Health' },
                      { key: 'include_trends', label: 'Trends Summary' },
                    ].map((item) => (
                      <label key={item.key} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={reportForm[item.key]}
                          onChange={(e) => setReportForm({ ...reportForm, [item.key]: e.target.checked })}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                        <span className="text-slate-700">{item.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* PDF Attachment Option */}
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-3">
                    <FileDown className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-slate-900">Attach PDF Report</p>
                      <p className="text-sm text-slate-500">Include a downloadable PDF version</p>
                    </div>
                  </div>
                  <Switch
                    checked={reportForm.include_pdf}
                    onCheckedChange={(checked) => setReportForm({ ...reportForm, include_pdf: checked })}
                    data-testid="report-pdf-switch"
                  />
                </div>
              </>
            )}

            {/* Send Now Button */}
            <div className="pt-4 border-t">
              <Button
                variant="outline"
                className="w-full"
                onClick={handleSendReportNow}
                disabled={sendingReport}
                data-testid="send-report-now-btn"
              >
                {sendingReport ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Report Now
              </Button>
              <p className="text-xs text-slate-500 text-center mt-2">
                Sends immediately to all recipients (or just to you if none configured)
              </p>
            </div>

            {/* Report History */}
            {reportHistory.length > 0 && (
              <div>
                <Label className="mb-2 block">Recent Reports</Label>
                <div className="max-h-32 overflow-y-auto space-y-2">
                  {reportHistory.slice(0, 5).map((log, i) => (
                    <div key={i} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded">
                      <div className="flex items-center gap-2">
                        <Clock className="w-3 h-3 text-slate-400" />
                        <span className="text-slate-600">
                          {new Date(log.sent_at).toLocaleDateString()}
                        </span>
                        {log.has_pdf && (
                          <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded">PDF</span>
                        )}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        log.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {log.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportModal(false)}>Cancel</Button>
            <Button onClick={handleSaveReportSchedule} disabled={saving} data-testid="save-report-schedule-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
